import { useMemo } from 'react';
import { __, _n, sprintf } from '@wordpress/i18n';
import { usePitchField } from './context.js';
import { useSelectionStore } from '@/hooks/useSelection.js';
import { usePrice, formatPrice } from '@/hooks/usePrice.js';

const EMPTY_OBJ = {};

export default function PriceDisplay() {
	const { mode, fieldConfig, tiles, fieldType, clearSelection } = usePitchField();

	// Selector returns the object-set — stable reference when this field's selection unchanged
	const selected = useSelectionStore( ( s ) => s.selected[ fieldType ] ?? EMPTY_OBJ );

	const price = usePrice( selected, fieldConfig, tiles );

	// Count selected "units": each bundle counts as 1, each standalone tile counts as 1
	const selectionCount = useMemo( () => {
		const ids = Object.keys( selected );
		if ( ! ids.length || ! tiles.length ) return 0;
		const selectedBundles = new Set();
		let   standaloneCount = 0;
		for ( const tile of tiles ) {
			if ( ! selected[ tile.id ] ) continue;
			if ( tile.bundleId ) selectedBundles.add( tile.bundleId );
			else                 standaloneCount++;
		}
		return selectedBundles.size + standaloneCount;
	}, [ selected, tiles ] );

	if ( mode !== 'interactive' ) return null;

	const isEmpty = Object.keys( selected ).length === 0;

	if ( isEmpty ) {
		return (
			<div className="wsp-price-display" aria-live="polite" aria-atomic="true">
				<span className="wsp-price-display__empty">
					{__( 'Select tiles to see the price.', 'web-sponsors-pitch' )}
				</span>
			</div>
		);
	}

	const { total, lines } = price;

	return (
		<div className="wsp-price-display" aria-live="polite" aria-atomic="true">
			<div className="wsp-price-display__header">
				<span className="wsp-price-display__count">
					{sprintf(
						_n( '%d tile selected', '%d tiles selected', selectionCount, 'web-sponsors-pitch' ),
						selectionCount
					)}
				</span>
				<button
					type="button"
					className="wsp-price-display__clear"
					onClick={clearSelection}
				>
					{__( 'Clear selection', 'web-sponsors-pitch' )}
				</button>
			</div>

			<table className="wsp-price-breakdown" aria-label={__( 'Price breakdown', 'web-sponsors-pitch' )}>
				<tbody>
					{lines.map( ( line, i ) => (
						<tr key={i} className={`wsp-price-breakdown__row wsp-price-breakdown__row--${ line.type }`}>
							<td className="wsp-price-breakdown__label">
								{line.type === 'bundle' ? (
									line.label
								) : (
									<>
										<span className="wsp-price-breakdown__count">{line.count}×</span>
										{' '}{line.label}
									</>
								)}
							</td>
							<td className="wsp-price-breakdown__unit">
								{line.type === 'bundle'
									? formatPrice( line.priceEach )
									: `${ formatPrice( line.priceEach ) } / Fl.`}
							</td>
							<td className="wsp-price-breakdown__subtotal">
								{formatPrice( line.subtotal )}
							</td>
						</tr>
					) )}
				</tbody>
				<tfoot>
					<tr className="wsp-price-breakdown__total">
						<td colSpan={2} className="wsp-price-breakdown__total-label">
							{__( 'Total', 'web-sponsors-pitch' )}
						</td>
						<td className="wsp-price-breakdown__total-amount">
							{formatPrice( total )}
						</td>
					</tr>
				</tfoot>
			</table>
		</div>
	);
}
